import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Page from '../../Components/Page';
import Button from '../../Components/Utils/Button';
import ReactTable from '../../Components/Utils/ReactTable';
import { dateFormatting } from '../../Utils';

import { useSelector } from 'react-redux';
import {
  useLazyRequestOrganizationsAdminQuery,
  useLazyRequestOrganizationsQuery
} from '../../redux/api/organizations';
import { selectAuthData } from '../../redux/selectors/auth';
import { useNavigate } from 'react-router-dom';
import { activeOrganizations } from '../../QueryFilters/queries.js';
import { selectUserType } from '../../redux/selectors/userType.js';
import { prepareData } from '../../Utils/dataFunctions.js';
import { organizationDetailFilter } from '../../QueryFilters/queries.js';
import Icon from '../../Components/Icon/index.jsx';

function Organizations() {
  //HOOKS
  const { isAdmin, isSuperAdmin, isProviderAdmin } = useSelector(selectUserType);

  const { user: { id: userId = '' } = {} } = useSelector(selectAuthData);
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [organizations, setOrganizations] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const cacheParams = { page, itemsPerPage, userId };
  const navigate = useNavigate();
  const [requestOrganizations, superAdminOrgs] = useLazyRequestOrganizationsQuery();
  const [requestAdminOrganizations, adminOrgs] = useLazyRequestOrganizationsAdminQuery();

  useEffect(() => {
    isSuperAdmin
      ? requestOrganizations(JSON.stringify(activeOrganizations)).then((response) => {
          if (response && response.data) {
            const updatedData = prepareData(response.data);
            setOrganizations(updatedData);
          }
        })
      : requestAdminOrganizations({
          userId,
          filter: JSON.stringify(organizationDetailFilter)
        }).then((response) => {
          if (response && response.data) {
            const updatedData = prepareData(response.data);
            setOrganizations(updatedData);
          }
        });
  }, [isSuperAdmin, isAdmin, isProviderAdmin]);

  const { t } = useTranslation();

  const columns = useMemo(() => {
    return [
      {
        accessor: '_id',
        disableSortBy: true
      },
      {
        Header: t('name'),
        accessor: 'name'
      },
      {
        Header: t('created_at'),
        accessor: 'createdAt',
        defaultSortBy: true,
        Cell: ({ cell }) => dateFormatting(cell.row.values.createdAt)
      },
      {
        Header: t('charging_points'),
        accessor: 'chargePoints',
        Cell: ({ cell }) => cell.row.values.chargePoints?.length
      },
      {
        Header: t('provider'),
        accessor: 'organizationParentOrganization',
        Cell: ({ cell }) =>
          cell.row.values?.organizationParentOrganization?.length > 0
            ? cell.row?.values?.organizationParentOrganization[0]?.parentOrganization.name
            : ''
      },
      {
        Header: t('contact_email'),
        accessor: 'organizationProperties',
        Cell: ({ cell }) => {
          return cell.row.values.organizationProperties?.contact_email;
        }
      },
      {
        Header: t('contact_phone'),
        Cell: ({ cell }) => {
          return cell.row.values.organizationProperties?.contact_phone;
        }
      },
      {
        Header: t('actions'),
        accessor: 'actions',
        className: 'table-action-cell',
        Cell: ({ cell }) => (
          <Button
            className='button button__alfa'
            onClick={() => {
              navigate(`/organizations/${cell.row.original.id}`);
            }}>
            {t('edit')}
          </Button>
        )
      }
    ];
  }, [organizations, t]);

  const headerItems = (
    <>
      <div className={'providers__search-bar'}>
        <Icon iconName={'search'} iconClass={'providers__search_icon'} />
        <input
          className='providers__search_input'
          value={searchTerm}
          placeholder={t('search')}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginRight: '10px' }}
        />
      </div>
      <Button className='button button__alfa' onClick={() => navigate(`/organizations/create`)}>
        {t('add_organizations')}
      </Button>
    </>
  );

  const filteredData = useMemo(() => {
    return organizations?.filter((item) => {
      const searchableString =
        `${item.name} ${item.organizationProperties?.contact_email} ${item.organizationProperties?.contact_phone}`.toLowerCase();
      return searchableString.includes(searchTerm.toLowerCase());
    });
  }, [organizations, searchTerm]);

  return (
    <Page title={t('organizations')} customHeaderItems={headerItems}>
      {organizations && (
        <ReactTable
          loading={organizations.isLoading}
          hiddenColumns={['id']}
          data={filteredData}
          columns={columns}
          setControlledPageSize={setItemsPerPage}
          setControlledPageIndex={setPage}
        />
      )}

      {organizations?.isError && <p>{organizations?.error}</p>}
    </Page>
  );
}

export default Organizations;

Organizations.propTypes = {
  cell: PropTypes.any
};
